import { isAfter, isBefore, isSameDay } from 'date-fns'
import dayTimesPoints from 'www/modules/Filters/common/constants/day-times'

type TimeParsed = [number, number]

/**
 * @param timeString - 'HH:MM'
 * @returns [hours, minutes]
 */

function getTimeParse(timeString: string): TimeParsed {
  return timeString.split(':').map(x => parseInt(x, 10)) as TimeParsed
}

/**
 * @param time - 'HH:MM'
 * @param intervalStart - 'HH:MM'
 * @param intervalEnd - 'HH:MM'
 */

function isTimeInInterval(time: string, intervalStart: string, intervalEnd: string): boolean {
  const [hours, minutes] = getTimeParse(time)
  const [endHours, endMinutes] = getTimeParse(intervalEnd)
  const [startHours, startMinutes] = getTimeParse(intervalStart)

  const compTime = hours * 60 + minutes
  const endTime = endHours * 60 + endMinutes
  const startTime = startHours * 60 + startMinutes

  return startTime <= compTime && compTime <= endTime
}

/**
 * @param dayStr - 'YYYY-MM-DD'
 * @param dateFromStr - 'YYYY-MM-DD'
 * @param dateToStr - 'YYYY-MM-DD'
 */

function isDayInInterval(dayStr: string, dateFromStr: string, dateToStr: string): boolean {
  const day = new Date(dayStr)
  const dateTo = new Date(dateToStr)
  const dateFrom = new Date(dateFromStr)
  const isDateInInterval = isAfter(day, dateFrom) && isBefore(day, dateTo)

  return isSameDay(day, dateFrom) || isSameDay(day, dateTo) || isDateInInterval
}

type DayIntervalKey = keyof typeof dayTimesPoints

type Slot = { time: string; free: boolean; duration: number }

/**
 * @param dayIntervalStr - 'morning' | 'evening' | 'afternoon' или через запятую
 * @param slots - [{ time, free, duration }, ...]
 * @returns true если хоть 1 слот в интервале
 */

function hasSlotsInTimeOfDay(dayIntervalStr: string = '', slots: Slot[] = []): boolean | undefined {
  const dayIntervalArray = dayIntervalStr.split(',')
  const hasSlotsInInterval = (intervalStart: DayIntervalKey, intervalEnd: DayIntervalKey): boolean => (
    slots.some(slot => isTimeInInterval(
      slot.time,
      dayTimesPoints[intervalStart],
      dayTimesPoints[intervalEnd],
    ))
  )

  if (dayIntervalArray.includes('morning')) {
    const result = hasSlotsInInterval('MORNING_START', 'MORNING_END')

    if (result) {
      return result
    }
  }

  if (dayIntervalArray.includes('afternoon')) {
    const result = hasSlotsInInterval('AFTERNOON_START', 'AFTERNOON_END')

    if (result) {
      return result
    }
  }

  if (dayIntervalArray.includes('evening')) {
    return hasSlotsInInterval('EVENING_START', 'EVENING_END')
  }
}

function getSelectedDateFromSearch(): {
  selectedDateFrom: string | undefined
  selectedDateTo: string | undefined
  selectedDayTime: string | undefined
  } {
  const queryString = decodeURIComponent(window.location.search)

  const patternDaytimeProp = /daytimes=(?<daytimes>[\w,]+)/
  const patternDateToProp = /date_to=(?<dateTo>\d{4}-\d{2}-\d{2})/
  const patternDateFromProp = /date_from=(?<dateFrom>\d{4}-\d{2}-\d{2})/

  const matchFrom = queryString.match(patternDateFromProp)
  const matchTo = queryString.match(patternDateToProp)
  const matchDaytime = queryString.match(patternDaytimeProp)

  return {
    selectedDateFrom: matchFrom?.groups?.dateFrom,
    selectedDateTo: matchTo?.groups?.dateTo,
    selectedDayTime: matchDaytime?.groups?.daytimes,
  }
}

type GetInitialDayParams = {
  daysWithSlots: string[]
  currentSlots: Record<string, Slot[]>
}

/**
 * @param daysWithSlots - массив строк с датами в формате ['YYYY-MM-DD', ...]
 * @param currentSlots - {YYYY-MM-DD: [{time, free, duration}, ...], ...}
 * @returns 'YYYY-MM-DD' или undefined
 */

function getInitialDay({
  daysWithSlots,
  currentSlots,
}: GetInitialDayParams = {} as GetInitialDayParams): string | undefined {
  const {
    selectedDateTo,
    selectedDayTime,
    selectedDateFrom,
  } = getSelectedDateFromSearch()

  if (selectedDateFrom && selectedDateTo && selectedDayTime) {
    return daysWithSlots.find(day => {
      const hasDayInInterval = isDayInInterval(day, selectedDateFrom, selectedDateTo)
      const hasSlotsInDayInterval = hasSlotsInTimeOfDay(selectedDayTime, currentSlots[day] || [])

      return hasDayInInterval && hasSlotsInDayInterval
    })
  }

  if (selectedDateFrom && selectedDateTo) {
    return daysWithSlots.find(day => isDayInInterval(day, selectedDateFrom, selectedDateTo))
  }

  if (selectedDayTime) {
    return daysWithSlots.find(day => hasSlotsInTimeOfDay(selectedDayTime, currentSlots[day] || []))
  }
}

export default getInitialDay
